import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { listAppUsers, createAppUser, loginAppUser, getStore, setUser, clearStore } from "@/lib/api";
import { User, ShieldCheck, Plus, Delete, ArrowLeft, LogOut } from "lucide-react";
import { toast, Toaster } from "sonner";
import InstallPrompt from "@/components/chalann/InstallPrompt";

export default function Login() {
  const nav = useNavigate();
  const store = getStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("staff");
  const [newPin, setNewPin] = useState("");

  useEffect(() => {
    if (!store?.id) { nav("/setup"); return; }
    load();
  }, [nav, store?.id]);

  const load = async () => {
    try {
      setLoading(true);
      const list = await listAppUsers(store.id);
      setUsers(list);
    } catch (e) { toast.error("No se cargaron los usuarios"); }
    finally { setLoading(false); }
  };

  const pressDigit = (d) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) attemptLogin(next);
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  const pickUser = async (u) => {
    if (u.pin_set) {
      // PIN configured → show pin modal
      setSelected(u);
      setPin("");
      return;
    }
    // No PIN yet → enter directly.
    try {
      const authed = await loginAppUser({ business_id: store.id, user_id: u.id, pin_code: "" });
      setUser(authed);
      toast.success(`¡Órale ${authed.name}!`);
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo entrar");
    }
  };

  const attemptLogin = async (fullPin) => {
    try {
      const u = await loginAppUser({ business_id: store.id, user_id: selected.id, pin_code: fullPin });
      setUser(u);
      toast.success(`¡Órale ${u.name}!`);
      nav("/dashboard");
    } catch (e) {
      toast.error("PIN incorrecto");
      setPin("");
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return toast.error("Escribe el nombre");
    if (newPin && !/^\d{4}$/.test(newPin)) return toast.error("Si pones contraseña, debe ser de 4 dígitos");
    try {
      await createAppUser({ business_id: store.id, name: newName.trim(), role: newRole, pin_code: newPin });
      setNewName(""); setNewPin(""); setNewRole("staff"); setAdding(false);
      toast.success(newPin ? "Usuario creado con contraseña" : "Usuario creado — podrá poner contraseña después");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 relative overflow-hidden" data-testid="login">
      <div className="grain-overlay" />
      <div className="glow-orange" style={{ top: "-30%", left: "-20%" }} />
      <div className="glow-lime" style={{ bottom: "-30%", right: "-20%" }} />

      <div className="relative w-full max-w-3xl">
        <InstallPrompt />
        <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2 flex items-center gap-2">
          / {store?.name} · tienda
          <button onClick={() => { clearStore(); nav("/setup"); }} className="ml-auto text-white/40 hover:text-white flex items-center gap-1" data-testid="login-switch-store">
            <LogOut size={11} /> Cambiar tienda
          </button>
        </p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-8">
          ¿Quién <span className="text-[#ff5a00]">está atendiendo?</span>
        </h1>

        {loading ? (
          <div className="text-center text-white/40 py-16">Cargando…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => pickUser(u)}
                className={`relative rounded-2xl border p-6 min-h-[160px] flex flex-col items-center justify-center gap-3 transition-colors duration-200 ${
                  u.role === "owner"
                    ? "border-[#ff5a00]/40 bg-[#ff5a00]/[0.06] hover:bg-[#ff5a00]/[0.12]"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                }`}
                data-testid={`user-tile-${u.id}`}
              >
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${u.role === "owner" ? "bg-[#ff5a00]/20 text-[#ff5a00]" : "bg-white/10 text-white/80"}`}>
                  {u.role === "owner" ? <ShieldCheck size={22} /> : <User size={22} />}
                </div>
                <div className="text-center">
                  <p className="font-display text-xl tracking-tighter leading-none">{u.name}</p>
                  <p className="font-mono text-[10px] tracking-widest uppercase text-white/40 mt-1">
                    {u.role === "owner" ? "Dueño" : "Empleado"}
                  </p>
                </div>
                {!u.pin_set && (
                  <span
                    className="absolute top-2 right-2 rounded-full bg-white/5 border border-white/10 text-white/50 px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest"
                    title="Sin contraseña — entra directo"
                  >
                    sin pin
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => setAdding(true)}
              className="rounded-2xl border border-dashed border-white/10 hover:border-[#ccff00] p-6 min-h-[160px] flex flex-col items-center justify-center gap-2 text-white/50 hover:text-[#ccff00] transition-colors"
              data-testid="user-add"
            >
              <Plus size={24} />
              <span className="text-sm">Agregar usuario</span>
            </button>
          </div>
        )}

        {/* PIN modal */}
        <AnimatePresence>
          {selected && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setSelected(null)}
            >
              <motion.div
                initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm bg-[#0a0a0a] border border-white/10 rounded-3xl p-8"
                data-testid="pin-modal"
              >
                <button onClick={() => setSelected(null)} className="text-white/50 hover:text-white text-sm flex items-center gap-1 mb-4">
                  <ArrowLeft size={14} /> Volver
                </button>
                <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-1">PIN de 4 dígitos</p>
                <h2 className="font-display text-3xl tracking-tighter mb-6">Hola, {selected.name}</h2>

                <div className="flex justify-center gap-3 mb-6">
                  {[0,1,2,3].map((i) => (
                    <span key={i}
                      className={`w-4 h-4 rounded-full transition-colors ${i < pin.length ? "bg-[#ccff00]" : "bg-white/15"}`}
                      data-testid={`pin-dot-${i}`}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[1,2,3,4,5,6,7,8,9].map((n) => (
                    <button key={n} onClick={() => pressDigit(String(n))}
                      className="py-4 rounded-xl border border-white/10 hover:bg-white/5 font-display text-2xl tracking-tighter"
                      data-testid={`pin-key-${n}`}
                    >{n}</button>
                  ))}
                  <span />
                  <button onClick={() => pressDigit("0")} className="py-4 rounded-xl border border-white/10 hover:bg-white/5 font-display text-2xl tracking-tighter" data-testid="pin-key-0">0</button>
                  <button onClick={backspace} className="py-4 rounded-xl border border-white/10 hover:bg-white/5 flex items-center justify-center text-white/60" data-testid="pin-back"><Delete size={18} /></button>
                </div>
                <p className="mt-4 text-[11px] font-mono text-white/40 text-center">
                  {selected.role === "owner" ? "Dueño: puede ver toda la información" : "Empleado: solo su turno"}
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Add-user modal */}
        <AnimatePresence>
          {adding && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setAdding(false)}
            >
              <motion.form
                initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                onSubmit={submitNew}
                className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-3xl p-8 space-y-4"
                data-testid="new-user-modal"
              >
                <h2 className="font-display text-2xl tracking-tighter">Nuevo usuario</h2>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Nombre</label>
                  <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Ej. Hijo / Empleado 1"
                    className="w-full mt-2 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#ff5a00]"
                    data-testid="new-user-name" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Rol</label>
                  <div className="mt-2 flex gap-2">
                    {["staff", "owner"].map((r) => (
                      <button key={r} type="button" onClick={() => setNewRole(r)}
                        className={`px-4 py-2 rounded-full text-sm border ${newRole === r ? "bg-white text-black border-white" : "border-white/10 text-white/70"}`}
                        data-testid={`new-user-role-${r}`}
                      >{r === "owner" ? "Dueño" : "Empleado"}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Contraseña (4 dígitos) — <span className="text-white/30 lowercase tracking-normal">opcional</span></label>
                  <input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric" placeholder="Déjalo vacío para entrar sin contraseña"
                    className="w-full mt-2 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-2xl tracking-widest focus:outline-none focus:border-[#ff5a00]"
                    data-testid="new-user-pin" />
                  <p className="text-white/40 text-[11px] mt-1">Podrá crearla después desde el ícono de contraseña en el dashboard.</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="btn-primary flex-1 justify-center" data-testid="new-user-submit">Crear</button>
                  <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancelar</button>
                </div>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}
